import { ColumnProps } from "@hightouch/formkit";
import { useMemo, useState } from "react";
import {
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";
import { JsonColumnProps } from "src/formkit/components/types";
import { ControllerField, ControllerRenderer, SharedProps } from ".";
import { Mapper } from "src/formkit/components";
import { captureException } from "src/lib/monitoring";
import {
  Box,
  Button,
  Combobox,
  GroupedCombobox,
  HiddenFormLabel,
  RefreshIcon,
  TextInput,
} from "@hightouch/htui";
import { Controller } from "react-hook-form";
import { getExpectedColumnOptions } from "src/events/utils/analyzed-event-schemas";

// Subtypes of the Column component
enum ColumnType {
  Mapper,
  Box,
  Text,
}

export const FKColumn = ({
  name,
  error,
  useStringColumnValue,
  advanced,
  templates,
  type,
  isDisabled,
}: ColumnProps & SharedProps & { useStringColumnValue: any; type: any }) => {
  const { columns, model } = useFormkitContext();
  const { reloadModel, loadingModel } = useFormkitDataContext();
  const [jsonColumnProperties, setJsonColumnProperties] =
    useState<JsonColumnProps>({
      selectedColumnProps: undefined,
      allColumnsProps: undefined,
    });
  const isEventModel = Boolean(model?.isEventModel);

  // Items in `columns` have `options` field as optional,
  // but `GroupedCombobox` expects it to be required
  const optionGroups = useMemo(() => {
    return (columns ?? []).map((group) => ({
      ...group,
      options: group.options ?? [],
    }));
  }, [columns]);

  const flatColumnOptions = useMemo(() => {
    return optionGroups
      .flatMap((group) => group.options ?? [])
      .map((o) => ({
        ...o,
        description: o.description ?? undefined,
        type: o.type ?? undefined,
      }));
  }, [optionGroups]);

  // Determine the kind of component that will be rendered
  const base = advanced
    ? ColumnType.Mapper
    : model
      ? ColumnType.Box
      : ColumnType.Text;

  // Define onchange
  const onChange = (field: ControllerField) => (value: string | undefined) => {
    if (!value) {
      field.onChange(advanced ? { type: "standard" } : null);
      return;
    }

    field.onChange(useStringColumnValue ? value : { from: value });
  };

  const renderer: Record<ColumnType, ControllerRenderer> = {
    [ColumnType.Mapper]: ({ field }) => (
      <Mapper
        isClearable={true}
        isDisabled={isDisabled}
        isError={Boolean(error)}
        jsonColumnProperties={jsonColumnProperties}
        placeholder="Select a value…"
        selectedOption={undefined}
        templates={templates ?? []}
        value={
          field.value
            ? field.value
            : advanced
              ? { type: "standard" }
              : undefined
        }
        onChange={(value) => {
          if (!value) {
            field.onChange({ type: "standard" });
            return;
          }
          field.onChange(value);
        }}
        onChangeJsonColumnProperties={setJsonColumnProperties}
        onReloadEligibleInlineMapperColumns={() => {
          captureException(
            new Error(
              "reloadJsonColumnProps called for column formkit component",
            ),
          );
        }}
      />
    ),
    [ColumnType.Box]: ({ field }) => (
      <Box alignItems="center" display="flex" gap={2}>
        {isEventModel ? (
          <Combobox
            emptyOptionsMessage="Type in a field name and Hightouch will use it as an expected column"
            isClearable={true}
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            isLoading={loadingModel}
            options={getExpectedColumnOptions(
              flatColumnOptions,
              useStringColumnValue ? field.value : field.value?.from,
            )}
            placeholder={
              flatColumnOptions.length
                ? "Select or enter a column…"
                : "Enter an expected column…"
            }
            supportsCreatableOptions={true}
            createOptionMessage={(value) =>
              `Select "${value}" as an expected column value…`
            }
            value={useStringColumnValue ? field.value : field.value?.from}
            onChange={(value) => onChange(field)(value || undefined)}
            onCreateOption={(value) => onChange(field)(value || undefined)}
          />
        ) : (
          <GroupedCombobox
            isClearable={true}
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            isLoading={loadingModel}
            optionGroups={optionGroups}
            placeholder="Select a column…"
            value={useStringColumnValue ? field.value : field.value?.from}
            onChange={onChange(field)}
          />
        )}

        <Button
          icon={RefreshIcon}
          onClick={reloadModel}
          isDisabled={isDisabled}
        >
          Refresh
        </Button>
      </Box>
    ),
    [ColumnType.Text]: ({ field }) => {
      return (
        <HiddenFormLabel label="Enter a column">
          <TextInput
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            placeholder="Enter a column…"
            value={field.value?.from}
            onChange={(event) => onChange(field)(event.target.value)}
            type={type}
          />
        </HiddenFormLabel>
      );
    },
  };

  return <Controller name={name} render={renderer[base]} />;
};
